/**
 * Deal document checklist rules (pure). Settlement documents live on the Settlement tab; everything else is grouped
 * by when it is needed. Requirements raised by a bank update or an operational step point at it with `sourceRef`.
 */
import type { Schema } from '@dealdesk/api-schema';

type Doc = Schema<'DealDocument'>;
type Stage = Schema<'DealStage'>;

export const DOCUMENT_GROUPS = ['OPS_REVIEW', 'SUBMITTED_TO_BANK', 'POST_APPROVAL'] as const;
export type DocumentGroup = (typeof DOCUMENT_GROUPS)[number];

const POST_APPROVAL: readonly Stage[] = ['APPROVED', 'POST_APPROVAL', 'BOOKED', 'DISBURSED'];
const PRE_BANK: readonly Stage[] = ['DRAFT', 'DOCUMENTS_PENDING', 'OPS_REVIEW', 'OPS_RETURNED'];

/** The checklist group of a requirement: before the bank, at submission / bank review, after approval. */
export function documentGroup(requiredBy: Stage): DocumentGroup {
  if (POST_APPROVAL.includes(requiredBy)) return 'POST_APPROVAL';
  if (PRE_BANK.includes(requiredBy)) return 'OPS_REVIEW';
  return 'SUBMITTED_TO_BANK';
}

/**
 * The deal checklist: settlement documents (letters, MC copy, clearance) are shown on the Settlement tab and the
 * signed copies of bank templates in the Bank forms section (A5).
 */
export const checklistDocuments = (docs: readonly Doc[]): Doc[] => docs.filter((d) => d.requirementSource !== 'settlement' && d.requirementSource !== 'bank_template');

/** An "Other document" a user added can be removed while no version of its file was approved (the server decides). */
export const canRemove = (d: Doc): boolean => d.requirementSource === 'custom' && d.removable === true;

/** Documents the bank asked for in one bank update (query / document request). */
export const requirementsOfBankUpdate = (docs: readonly Doc[], updateId: string): Doc[] => docs.filter((d) => d.requirementSource === 'bank_update' && d.sourceRef === updateId);

/** Documents an operational step needs (raised by the step's configuration). */
export const requirementsOfStep = (docs: readonly Doc[], stepId: string): Doc[] => docs.filter((d) => d.requirementSource === 'operation' && d.sourceRef === stepId);

/** A requirement can be waived while it is still open (not approved). */
export const canWaive = (d: Doc): boolean => d.status !== 'approved';

/** A new version can be uploaded in every state except while the current file is being scanned. */
export const canReplace = (d: Doc): boolean => d.status !== 'scanning';

/** The current file was quarantined or could not be scanned. */
export const fileRefused = (d: Pick<Doc, 'scanStatus'>): boolean => d.scanStatus === 'infected' || d.scanStatus === 'failed';

/** Documents the bank asked for that are not approved yet (shown when replying to a bank query). */
export const openBankRequests = (docs: readonly Doc[]): Doc[] => docs.filter((d) => d.requirementSource === 'bank_update' && d.status !== 'approved');
